/**
 * MCP tool-call activity — API client.
 *
 * Thin fetch wrapper matching the `meta-activity-api.ts` / `audit-api.ts`
 * conventions (RTK Query is intentionally NOT used here, for consistency with
 * the rest of the agency surface area). All requests use the agency Bearer
 * token (the per-employee `emp_` key) sourced from Redux
 * (`state.auth.agency.accessToken`) — never localStorage.
 *
 * Backend contract:
 *   GET /api/v1/agency/mcp-calls
 *       ?shop=&platform=&direction=&status=&from=&to=&page=&per_page=
 *     → { data: [ McpCallEntry ], meta: { total, per_page, current_page, last_page } }
 *
 * Org-scoped: the backend scopes rows by organisation_id + accessible shop ids
 * (exactly like the Meta API activity feed) and enforces owner/team_lead role
 * (403 otherwise).
 *
 * Responses are unwrapped defensively: the backend may return the list payload
 * bare (a top-level array) OR wrapped under a `{ data, meta }` envelope.
 */
import { CENTRAL_API_BASE_URL } from '../../constants/central-api';
import store from '../../../store/store';

// ---------- shared types ----------

/** Direction of the tool call — a read vs a mutating write. */
export type McpCallDirection = 'read' | 'write';

/** Per-call outcome. */
export type McpCallStatus = 'ok' | 'denied' | 'error' | 'proposed';

/** A single MCP tool-call entry. */
export interface McpCallEntry {
  id: string | number;
  organisation_id: string | number | null;
  shop_id: string | number | null;
  /** Human-readable shop name when the backend can resolve it. */
  shop_name?: string | null;
  /** Shop slug, when present (used to humanise against the accessible-shop list). */
  shop_slug?: string | null;
  employee_id: string | number | null;
  /** Employee email resolved by the backend (org-scoped, survives soft-deletes). */
  employee_email?: string | null;
  /** Human-readable employee name/email when the backend can resolve it. */
  employee_name?: string | null;
  platform: string;
  connection_id: string | number | null;
  tool_name: string;
  direction: McpCallDirection;
  status: McpCallStatus;
  /** Coarse fixed outcome code (e.g. 'upstream_error') — never a message/payload. */
  error_code: string | null;
  duration_ms: number | null;
  created_at: string; // ISO8601
}

export interface McpCallMeta {
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
}

export interface McpCallResponse {
  data: McpCallEntry[];
  meta: McpCallMeta;
}

/** Filters accepted by `listMcpCalls`. All fields optional. */
export interface McpCallFilters {
  shop?: string;
  platform?: string;
  direction?: McpCallDirection | '';
  status?: McpCallStatus | '';
  /** ISO date (YYYY-MM-DD) or ISO8601. */
  from?: string;
  /** ISO date (YYYY-MM-DD) or ISO8601. */
  to?: string;
  page?: number;
  per_page?: number;
}

// ---------- internal helpers ----------

function buildHeaders(token: string | undefined): Record<string, string> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

async function parseJson<T>(response: Response): Promise<T> {
  const text = await response.text();
  if (!text) {
    return {} as T;
  }
  try {
    return JSON.parse(text) as T;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[mcp-calls-api] failed to parse JSON', err, text);
    throw new Error('Invalid response from server.');
  }
}

async function request<T>(
  path: string,
  token: string | undefined,
  init: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    ...buildHeaders(token),
    ...((init.headers as Record<string, string>) || {}),
  };
  if (init.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${CENTRAL_API_BASE_URL}${path}`, {
    ...init,
    headers,
  });

  if (!response.ok) {
    let detail = '';
    try {
      const body = await parseJson<{ message?: string; error?: string }>(
        response
      );
      detail = body?.message || body?.error || '';
    } catch {
      // swallow — fall back to status text
    }
    const err = new Error(
      detail || `Request failed (${response.status} ${response.statusText})`
    ) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }

  return parseJson<T>(response);
}

function buildListQuery(filters: McpCallFilters): string {
  const params = new URLSearchParams();
  if (filters.shop) params.set('shop', filters.shop);
  if (filters.platform) params.set('platform', filters.platform);
  if (filters.direction) params.set('direction', filters.direction);
  if (filters.status) params.set('status', filters.status);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.page && filters.page > 0) {
    params.set('page', String(filters.page));
  }
  if (filters.per_page && filters.per_page > 0) {
    params.set('per_page', String(filters.per_page));
  }
  return params.toString();
}

/**
 * Normalise the list response so callers always get `{ data, meta }`.
 *
 * Accepts:
 *   - the canonical `{ data: [...], meta: {...} }` envelope, or
 *   - a bare array of entries (defensive — backend variants), in which case we
 *     synthesise a single-page meta block.
 */
function normaliseList(
  raw: McpCallResponse | McpCallEntry[] | { data?: McpCallEntry[] },
  requestedPage: number,
  requestedPerPage: number
): McpCallResponse {
  // Bare array form.
  if (Array.isArray(raw)) {
    return {
      data: raw,
      meta: {
        total: raw.length,
        per_page: requestedPerPage,
        current_page: requestedPage,
        last_page: 1,
      },
    };
  }

  // Descend the ApiResponse envelope `{ success, message, data: <payload> }`.
  // central-api (ApiResponse::success) nests the list one level deeper than the
  // bare `{ data, meta }` this normaliser originally assumed. When `.data` is a
  // non-array object, the real list payload lives inside it; when `.data` is
  // already an array (or absent), the payload is top-level and we leave it
  // untouched — so bare and wrapped shapes both work and this can't regress.
  const obj = (raw ?? {}) as { data?: unknown; meta?: Partial<McpCallMeta> };
  const descended: unknown =
    obj.data != null && typeof obj.data === 'object' && !Array.isArray(obj.data)
      ? obj.data
      : obj;

  // The descended payload may itself be a bare array.
  if (Array.isArray(descended)) {
    return {
      data: descended as McpCallEntry[],
      meta: {
        total: descended.length,
        per_page: requestedPerPage,
        current_page: requestedPage,
        last_page: 1,
      },
    };
  }

  const inner = descended as { data?: unknown; meta?: Partial<McpCallMeta> };
  const data = Array.isArray(inner.data) ? (inner.data as McpCallEntry[]) : [];
  const meta: McpCallMeta = {
    total: inner.meta?.total ?? data.length,
    per_page: inner.meta?.per_page ?? requestedPerPage,
    current_page: inner.meta?.current_page ?? requestedPage,
    last_page: inner.meta?.last_page ?? 1,
  };
  return { data, meta };
}

// ---------- public API ----------

/**
 * Fetch a paginated, filtered slice of the agency's MCP tool-call feed.
 * Org-scoped by the backend; owner/team_lead role enforced (403 otherwise).
 */
export async function listMcpCalls(
  filters: McpCallFilters = {},
  token?: string
): Promise<McpCallResponse> {
  const t = token ?? store.getState().auth.agency.accessToken;
  const requestedPage = filters.page && filters.page > 0 ? filters.page : 1;
  const requestedPerPage =
    filters.per_page && filters.per_page > 0 ? filters.per_page : 25;
  const qs = buildListQuery({
    ...filters,
    page: requestedPage,
    per_page: requestedPerPage,
  });
  const raw = await request<
    McpCallResponse | McpCallEntry[] | { data?: McpCallEntry[] }
  >(`agency/mcp-calls?${qs}`, t, { method: 'GET' });
  return normaliseList(raw, requestedPage, requestedPerPage);
}
